"use client";

import React from "react";
import type { QuestionConfig, AnswerData, ValidationRules } from "../types";
import { useTranslation } from '@/app/i18n/client';

interface MatrixQuestionProps {
    config: QuestionConfig;
    value: AnswerData;
    onChange: (value: AnswerData) => void;
    validationRules?: ValidationRules;
    error?: string;
    onEnterPress?: () => void; // Callback when Enter is pressed after selection
}

export default function MatrixQuestion({ config, value, onChange, validationRules, error, onEnterPress }: MatrixQuestionProps) {
    const { lang } = useTranslation();

    // Helper function to get text in correct language
    const getText = (th: string | undefined, en: string | undefined): string => {
      if (lang === 'en') {
        return en || th || '';
      }
      return th || en || '';
    };

    const matrixData = value?.matrix || {};
    const getRowKey = (row: { id?: string; value?: string }) => row.id || row.value || "";
    const getColumnKey = (column: { id?: string; value?: string }) => column.id || column.value || "";

    const handleChange = (row: any, column: any) => {
        const rowKey = getRowKey(row);
        const columnKey = getColumnKey(column);
        
        const newMatrix = {
            ...matrixData,
            [rowKey]: columnKey,
        };
        onChange({ matrix: newMatrix });
    };

    const handleRadioKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Trigger validation and next when Enter is pressed on any radio input
        if (e.key === 'Enter') {
            e.preventDefault();
            const rows = config.rows || [];
            const allRowsFilled = rows.every((row: any) => {
                const rowKey = getRowKey(row);
                return matrixData[rowKey] !== undefined && matrixData[rowKey] !== null && matrixData[rowKey] !== '';
            });
            
            if (allRowsFilled && onEnterPress) {
                onEnterPress();
            }
        }
    };

    if (!config.rows || !config.columns) {
        return <div className="text-red-600">Invalid matrix configuration</div>;
    }

    // Mobile view (stacked)
    const MobileView = () => (
        <div className="space-y-6 md:hidden">
            {config.rows?.map((row) => {
                const rowKey = getRowKey(row);
                return (
                    <div key={rowKey} className="card">
                        <h4 className="font-medium text-gray-900 mb-3">{getText(row.label, row.labelEn)}</h4>
                        <div className="space-y-2">
                            {config.columns?.map((column) => {
                                const columnKey = getColumnKey(column);
                                const isSelected = matrixData[rowKey] === columnKey;
                                return (
                                    <label
                                        key={column.value}
                                        className={`flex items-center py-1.5 rounded-lg cursor-pointer transition-all duration-200 ${
                                            isSelected
                                                ? "border-primary-600 bg-primary-50"
                                                : "border-gray-200 hover:border-primary-300"
                                        }`}>
                                        <input
                                            type="radio"
                                            name={`matrix-mobile-row-${rowKey}`}
                                            value={column.value}
                                            checked={isSelected}
                                            onChange={() => handleChange(row, column)}
                                            onKeyDown={handleRadioKeyDown}
                                            className="sr-only"
                                        />
                                        <span
                                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                                isSelected
                                                    ? "bg-[#0F8F80] border-2 border-[#0F8F80]"
                                                    : "bg-white border-2 border-[#D1D5DB]"
                                            }`}>
                                            {isSelected ? (
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M16.6666 5L7.49998 14.1667L3.33331 10"
                                                        stroke="#FFFFFF"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    width="20"
                                                    height="20"
                                                    viewBox="0 0 20 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M16.6666 5L7.49998 14.1667L3.33331 10"
                                                        stroke="#B3B3B3"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </span>
                                        <span className="ml-3 text-sm">{getText(column.label, column.labelEn)}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // Desktop view (table)
    const DesktopView = () => (
        <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse border-spacing-0">
                <thead>
                    <tr className="bg-[#CCE5DD]">
                        <th className="p-4 text-left font-medium text-sm text-[#2E2E2E] border-r-2 border-[#e3f3f0]">
                            {/* Empty header for row labels */}
                        </th>
                        {config.columns?.map((column, index) => (
                            <th
                                key={column.value}
                                className={`p-4 text-center font-normal text-sm text-[#2E2E2E] ${
                                    index < config.columns!.length - 1 ? "border-r-2 border-[#e3f3f0]" : ""
                                }`}>
                                {getText(column.label, column.labelEn)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {config.rows?.map((row, rowIndex) => {
                        const rowKey = getRowKey(row);
                        const isColoredRow = rowIndex % 2 === 1;
                        return (
                            <tr key={rowKey}>
                                <td
                                    className={`p-4 font-normal text-[#2E2E2E] min-w-[260px] ${
                                        isColoredRow
                                            ? "bg-[#CCE5DD] border-r-2 border-[#e3f3f0]"
                                            : "bg-transparent border-r-2 border-transparent"
                                    }`}>
                                    {getText(row.label, row.labelEn)}
                                </td>
                                {config.columns?.map((column, colIndex) => {
                                    const columnKey = getColumnKey(column);
                                    const isSelected = matrixData[rowKey] === columnKey;
                                    return (
                                        <td
                                            key={column.value}
                                            className={`p-4 text-center ${
                                                isColoredRow
                                                    ? colIndex < config.columns!.length - 1
                                                        ? "bg-[#CCE5DD] border-r-2 border-[#e3f3f0]"
                                                        : "bg-[#CCE5DD]"
                                                    : "bg-transparent"
                                            }`}>
                                            <label className="inline-flex items-center justify-center cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name={`matrix-desktop-row-${rowKey}`}
                                                    value={column.value}
                                                    checked={isSelected}
                                                    onChange={() => handleChange(row, column)}
                                                    onKeyDown={handleRadioKeyDown}
                                                    className="sr-only"
                                                />
                                                <span
                                                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                                        isSelected
                                                            ? "bg-[#0F8F80] border-2 border-[#0F8F80]"
                                                            : "bg-white border-2 border-[#D1D5DB]"
                                                    }`}>
                                                    {isSelected ? (
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 20 20"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M16.6666 5L7.49998 14.1667L3.33331 10"
                                                                stroke="#FFFFFF"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 20 20"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M16.6666 5L7.49998 14.1667L3.33331 10"
                                                                stroke="#B3B3B3"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                            </label>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="space-y-4 mt-8" tabIndex={0}>
            <MobileView />
            <DesktopView />
            {error && <p className="error-text">{error}</p>}
        </div>
    );
}
